import React, { useEffect } from 'react';
import { addDays, eachDayOfInterval, format, isSameDay, startOfWeek, subDays } from 'date-fns';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { Popover } from '@/components/atoms/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

interface CalendarStripProps {
  className?: string;
  /*
   * The initial week to show in the calendar strip
   */
  defaultWeek?: Date;
  /*
   * The selected date in the calendar strip
   */
  selected?: Date | null;
  /*
   * Apply the `disabled` modifier to the matching days.
   * Disabled days cannot be selected
   */
  disabled?: (date: Date) => boolean;
  /*
   * The function to handle the day selection
   */
  onSelect?: (date: Date) => void;
}

export function CalendarStrip({ className, defaultWeek, selected, disabled, onSelect }: CalendarStripProps) {
  // Calculate the week start date (monday)
  const [weekStartDate, setWeekStartDate] = React.useState(() => {
    const referenceDate = selected || defaultWeek || new Date();
    return startOfWeek(referenceDate, { weekStartsOn: 1 }); // 1 = Monday
  });

  useEffect(() => {
    if (selected) {
      startOfWeek(selected, { weekStartsOn: 1 });
    }
  }, [selected]);

  const days = React.useMemo(() => {
    return eachDayOfInterval({
      start: weekStartDate,
      end: addDays(weekStartDate, 6),
    });
  }, [weekStartDate]);

  const onNextWeek = React.useCallback(() => {
    setWeekStartDate(addDays(weekStartDate, 7));
  }, [weekStartDate]);

  const onPrevWeek = React.useCallback(() => {
    setWeekStartDate(subDays(weekStartDate, 7));
  }, [weekStartDate]);

  const isDaySelected = React.useCallback(
    (day: Date) => {
      if (!selected) return false;
      return isSameDay(day, selected);
    },
    [selected]
  );

  return (
    <div className={cn('w-full overflow-hidden rounded-lg border-[0.5px] border-border shadow-md', className)}>
      {/* Header */}
      <div className='flex w-full items-center border-b-[0.5px]'>
        {/* Month */}
        <div className='px-2 text-sm font-medium'>{format(weekStartDate, 'MMMM')}</div>
        <div className='ml-auto'>
          <Button variant='ghost' size='icon' onClick={onPrevWeek} className='rounded-none'>
            <ChevronLeftIcon className='h-4 w-4' />
          </Button>
          <Popover
            trigger={
              <Button variant='ghost' size='icon' className='rounded-none'>
                <CalendarIcon className='h-4 w-4' />
              </Button>
            }
            align='end'
          >
            <Calendar
              mode='single'
              selected={selected ?? undefined}
              onSelect={(date) => {
                if (date && onSelect) {
                  onSelect(date);
                }
              }}
              weekStartsOn={1}
            />
          </Popover>
          <Button variant='ghost' size='icon' onClick={onNextWeek} className='rounded-none rounded-tr-lg'>
            <ChevronRightIcon className='h-4 w-4' />
          </Button>
        </div>
      </div>
      {/* Content */}
      <div className='flex w-full justify-between p-1'>
        {days.map((day) => (
          <DayItem
            key={day.toISOString()}
            day={day}
            disabled={disabled?.(day)}
            selected={isDaySelected(day)}
            onClick={() => onSelect?.(day)}
          />
        ))}
      </div>
    </div>
  );
}

interface DayItemProps {
  day: Date;
  disabled?: boolean;
  selected?: boolean;
  onClick?: () => void;
}

function DayItem({ day, disabled, selected, onClick }: DayItemProps) {
  return (
    <button
      className={cn(
        'group flex flex-col items-center space-y-2 rounded-md p-1 transition-colors hover:bg-accent',
        'disabled:pointer-events-none disabled:opacity-50'
      )}
      disabled={disabled}
      onClick={onClick}
    >
      <div className='flex h-6 items-center justify-center text-xs'>{format(day, 'EE')}</div>
      <div
        className={cn(
          'flex size-8 items-center justify-center rounded-full',
          selected && 'bg-primary text-primary-foreground'
        )}
      >
        {format(day, 'd')}
      </div>
    </button>
  );
}
